import { getMaterialImage } from 'boardCanvas/Materials';
import { Renderable } from 'Types';

import {
  getRowDrawingOffset,
  getRowDrawingSkewing,
  memoize,
  scaleRectangle,
} from 'utils/utils';

export function getContextX(
  canvas: HTMLCanvasElement
): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');

  if (ctx == null) {
    throw Error('Cannot find canvas context');
  }

  return ctx;
}

export function clearCanvas(canvas: HTMLCanvasElement) {
  const ctx = getContextX(canvas);

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.beginPath(); // This clears any current paths
}

function drawGrainImage(
  canvas: HTMLCanvasElement,
  renderable: Renderable,
  pxPerInch: number
) {
  const { material, rect } = renderable;
  if (material == null) {
    return;
  }

  const ctx = getContextX(canvas);
  const image = getMaterialImage(material);

  const scaledRect = scaleRectangle(rect, pxPerInch);

  const { initialX, initialY } = getInitialImageOffset(
    renderable,
    pxPerInch,
    image.width,
    image.height
  );

  // How many rows/cols of images do we have to draw to cover the whole rect?
  const numHorizontal = Math.ceil((scaledRect.width + initialX) / image.width);
  const numVertical = Math.ceil((scaledRect.height + initialY) / image.height);

  for (let i = 0; i < numHorizontal; ++i) {
    for (let j = 0; j < numVertical; ++j) {
      const targetX =
        i === 0
          ? scaledRect.left
          : -initialX + i * image.width + scaledRect.left;
      const targetY =
        j === 0
          ? scaledRect.top
          : -initialY + j * image.height + scaledRect.top;
      const sourceX = i === 0 ? initialX : 0;
      const sourceY = j === 0 ? initialY : 0;
      const sourceWidth = Math.min(scaledRect.width, image.width - sourceX);
      const sourceHeight = Math.min(scaledRect.height, image.height - sourceY);
      // Don't stretch or reduce the image
      const targetWidth = sourceWidth;
      const targetHeight = sourceHeight;

      const flipX = i % 2;
      const flipY = j % 2;

      const scaleH = flipX ? -1 : 1;
      const scaleV = flipY ? -1 : 1;

      ctx.save();
      // Set scale to flip the image
      ctx.scale(scaleH, scaleV);

      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        flipX ? -targetX - sourceWidth : targetX,
        flipY ? -targetY - sourceHeight : targetY,
        targetWidth,
        targetHeight
      );

      ctx.restore();
    }
  }
}

const getInitialImageOffset = memoize(
  (
    renderable: Renderable,
    pxPerInch: number,
    width: number,
    height: number
  ) => {
    const initialX = Math.round(Math.random() * (width / 2));
    const initialY = Math.round(Math.random() * (height / 2));

    return { initialX, initialY };
  },
  ({ material, metadata, rect }, pxPerInch) => {
    return [material, metadata, rect.height * pxPerInch, rect.width * pxPerInch]
      .map((_) => JSON.stringify(_))
      .join('-');
  }
);

export function drawRenderables(
  canvas: HTMLCanvasElement,
  renderables: Array<Renderable>,
  pxPerInch: number
) {
  for (const renderable of renderables) {
    if (renderable == null) {
      return;
    }

    applyTransform(canvas, renderable, pxPerInch);
    drawGrainImage(canvas, renderable, pxPerInch);
    getContextX(canvas).resetTransform();
  }
}

export function highlightRect(
  canvas: HTMLCanvasElement,
  renderable: Renderable,
  pxPerInch: number
) {
  const ctx = getContextX(canvas);
  const rect = renderable.rect;

  ctx.moveTo(rect.left * pxPerInch, rect.top * pxPerInch);

  applyTransform(canvas, renderable, pxPerInch);
  ctx.rect(
    rect.left * pxPerInch,
    rect.top * pxPerInch,
    rect.width * pxPerInch,
    rect.height * pxPerInch
  );
  ctx.resetTransform();
}

function applyTransform(
  canvas: HTMLCanvasElement,
  renderable: Renderable,
  pxPerInch: number
) {
  const { rect, rotate } = renderable;

  if (rotate) {
    const skewing = getRowDrawingSkewing(rotate);
    const offset = getRowDrawingOffset(rotate, rect);

    const transform: [number, number, number, number, number, number] = [
      1,
      0,
      skewing,
      1,
      offset,
      0,
    ];

    const scaledTransform: [number, number, number, number, number, number] = [
      ...transform,
    ];
    scaledTransform[4] *= pxPerInch;
    scaledTransform[5] *= pxPerInch;

    getContextX(canvas).setTransform(...scaledTransform);
  }
}
